import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { NavigationProp, RouteProp, useNavigation, useNavigationState, useRoute } from '@react-navigation/native';
import { useForm, useWatch } from 'react-hook-form';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { translate } from '../../../services/translate';
import { crmService } from '../../../services/central-api/crm';
import { ClientError, InvalidValue } from '../../../services/central-api/base-service';
import { CRMParamList } from '../../../typings/Params';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import CustomButton from '../../../components/CustomButton';
import Divider from '../../../components/Divider';
import FormInput from '../../../components/FormInput';
import FormDropDownPicker from '../../../components/FormDropDownPicker';
import FormSwitch from '../../../components/FormSwitch';
import MyAppText from '../../../components/MyAppText';
import { Tabs } from '../../../components/Tabs';

interface CompanyCameraConfigurationFormFields {
    overlayText: string;
    frameRate: string;
    videoBitRate: string;
    videoCodec: CameraVideoCodec;
    resolution: CameraResolution;
    lprWebhookUrl: string;
    facialWebhookUrl: string;
    areaInvasionWebhookUrl: string;
    createRtspWatcher: boolean;
    adminPasswordRotation: boolean;
}

const DEFAULT_FORM_VALUES: CompanyCameraConfigurationFormFields = {
    overlayText: '',
    frameRate: '12',
    videoBitRate: '1024',
    videoCodec: 'h.265',
    resolution: '1920x1080',
    lprWebhookUrl: '',
    facialWebhookUrl: '',
    areaInvasionWebhookUrl: '',
    createRtspWatcher: true,
    adminPasswordRotation: true,
};

const videoCodecItems = [
    { label: 'H.264', value: 'h.264' },
    { label: 'H.265', value: 'h.265' },
];

const resolutionItems = [
    { label: '1280x720', value: '1280x720' },
    { label: '1920x1080', value: '1920x1080' },
    { label: '2560x1440', value: '2560x1440' },
    { label: '3840x2160', value: '3840x2160' },
];

function isValidWebhookUrl(value: string) {
    const trimmedValue = value.trim();

    if (!/^https?:\/\//i.test(trimmedValue)) {
        return false;
    }

    try {
        return new URL(trimmedValue).hostname.replace(/^\[|\]$/g, '').length > 0;
    } catch {
        return false;
    }
}

function SectionHeader({ title, children, viewZIndex, childrenZIndex }: {
    title: string;
    children: ReactNode;
    viewZIndex?: number;
    childrenZIndex?: number;
}) {
    const { styles, theme } = useStyles(styleSheet);

    return <View style={{ flexDirection: 'column', rowGap: 16, zIndex: viewZIndex }}>
        <View style={{ flexDirection: 'column', rowGap: 8, zIndex: 2 }}>
            <MyAppText style={styles.sectionHeaderTitle}>{title}</MyAppText>
            <Divider style={{ zIndex: -1 }} color={theme.colors.primary['200']} />
        </View>
        <View style={{ zIndex: childrenZIndex ?? 1, gap: 4 }}>
            {children}
        </View>
    </View>;
}

export default function CompanyCameraConfigurationDetails() {
    const { styles, theme } = useStyles(styleSheet);
    const route = useRoute<RouteProp<CRMParamList, 'CompanyCameraConfigurationDetails'>>();
    const navigation = useNavigation<NavigationProp<CRMParamList>>();
    const index = useNavigationState(state => state);

    const [isLoading, setIsLoading] = useState(false);
    const [companyName, setCompanyName] = useState('');

    const form = useForm<CompanyCameraConfigurationFormFields>({
        defaultValues: DEFAULT_FORM_VALUES,
        mode: 'onChange',
    });

    const formValues = useWatch({ control: form.control });

    const isFormValid = useMemo(() => {
        const frameRate = Number(formValues.frameRate);
        if (!Number.isInteger(frameRate) || frameRate < 1 || frameRate > 60) {
            return false;
        }

        const videoBitRate = Number(formValues.videoBitRate);
        if (!Number.isInteger(videoBitRate) || videoBitRate < 128 || videoBitRate > 16384) {
            return false;
        }

        if (!formValues.videoCodec || !formValues.resolution) {
            return false;
        }

        if ((formValues.overlayText ?? '').length > 44) {
            return false;
        }

        const webhookUrls = [formValues.lprWebhookUrl, formValues.facialWebhookUrl, formValues.areaInvasionWebhookUrl];
        return webhookUrls.every(url => isValidWebhookUrl(url ?? ''));
    }, [formValues]);

    useEffect(() => {
        getCompanyCameraConfiguration();
    }, [route.params?.companyId]);

    function goBack() {
        if (index.routes.length > 1) {
            return navigation.goBack();
        }

        navigation.navigate('CompaniesCameraConfiguration', { page: 1, limit: 25 });
    }

    async function getCompanyCameraConfiguration() {
        try {
            setIsLoading(true);
            const configuration = await crmService.getCompanyCameraConfiguration(route.params.companyId);

            setCompanyName(configuration.company.name);
            form.reset({
                overlayText: configuration.overlayText,
                frameRate: String(configuration.frameRate),
                videoBitRate: String(configuration.videoBitRate),
                videoCodec: configuration.videoCodec,
                resolution: configuration.resolution,
                lprWebhookUrl: configuration.lprWebhookUrl,
                facialWebhookUrl: configuration.facialWebhookUrl,
                areaInvasionWebhookUrl: configuration.areaInvasionWebhookUrl,
                createRtspWatcher: configuration.createRtspWatcher,
                adminPasswordRotation: configuration.adminPasswordRotation,
            });
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    async function save(formData: CompanyCameraConfigurationFormFields) {
        try {
            setIsLoading(true);
            await crmService.updateCompanyCameraConfiguration(route.params.companyId, {
                overlayText: formData.overlayText,
                frameRate: Number(formData.frameRate),
                videoBitRate: Number(formData.videoBitRate),
                videoCodec: formData.videoCodec,
                resolution: formData.resolution,
                lprWebhookUrl: formData.lprWebhookUrl.trim(),
                facialWebhookUrl: formData.facialWebhookUrl.trim(),
                areaInvasionWebhookUrl: formData.areaInvasionWebhookUrl.trim(),
                createRtspWatcher: formData.createRtspWatcher,
                adminPasswordRotation: formData.adminPasswordRotation,
            });

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('configurationUpdatedSuccessfully'),
            });
            goBack();
        } catch (err) {
            if (err instanceof InvalidValue) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('configurationUpdateFailed'),
                    text2: translate(err.message),
                });
            }

            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('configurationUpdateFailed'),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('configurationUpdateFailed'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Container>
            <Header
                title={translate('editConfiguration')}
                goBackAction={goBack}
                actions={<CustomButton
                    onPress={form.handleSubmit(save)}
                    text={translate('save')}
                    disabled={isLoading || !isFormValid}
                />}
            />

            <Tabs
                tabs={[{
                    key: 'company_info',
                    label: translate('companyInformation'),
                }]}>
                {() => <Card>
                    <ScrollView style={{ padding: 24 }} contentContainerStyle={{ rowGap: 32 }}>
                        {isLoading &&
                            <ActivityIndicator size='small' color={theme.colors.loadingColor} />
                        }

                        <SectionHeader title={translate('companyData')} viewZIndex={3}>
                            <View style={styles.fieldsRow}>
                                <View style={styles.field}>
                                    <MyAppText style={styles.readOnlyLabel}>{translate('company')}</MyAppText>
                                    <MyAppText style={styles.readOnlyValue}>{companyName}</MyAppText>
                                </View>
                            </View>
                        </SectionHeader>

                        <SectionHeader title={translate('cameraConfigurations')} viewZIndex={2} childrenZIndex={2}>
                            <View style={styles.fieldsRow}>
                                <FormInput
                                    label={translate('watermark')}
                                    placeholder={translate('watermark')}
                                    form={form}
                                    fieldName='overlayText'
                                    viewStyle={styles.field}
                                    maxLength={44}
                                />
                                <FormInput
                                    label={translate('fps')}
                                    placeholder={translate('fps')}
                                    form={form}
                                    fieldName='frameRate'
                                    mode='NUMBER'
                                    minValue={1}
                                    maxValue={60}
                                    required
                                    viewStyle={styles.field}
                                />
                                <FormInput
                                    label={translate('videoBitRate')}
                                    placeholder={translate('videoBitRate')}
                                    form={form}
                                    fieldName='videoBitRate'
                                    mode='NUMBER'
                                    minValue={128}
                                    maxValue={16384}
                                    required
                                    viewStyle={styles.field}
                                />
                            </View>
                            <View style={[styles.fieldsRow, { zIndex: 2 }]}>
                                <FormDropDownPicker
                                    label={translate('videoCodec')}
                                    items={videoCodecItems}
                                    form={form}
                                    fieldName='videoCodec'
                                    zIndex={3}
                                    required
                                    viewStyle={styles.field}
                                />
                                <FormDropDownPicker
                                    label={translate('resolution')}
                                    items={resolutionItems}
                                    form={form}
                                    fieldName='resolution'
                                    zIndex={2}
                                    required
                                    viewStyle={styles.field}
                                />
                            </View>
                        </SectionHeader>

                        <SectionHeader title={translate('webhookUrls')} viewZIndex={1}>
                            <View style={styles.fieldsRow}>
                                <FormInput
                                    label={translate('lprWebhookUrl')}
                                    placeholder='https://lpr-v4.sentinelx.com.br'
                                    form={form}
                                    fieldName='lprWebhookUrl'
                                    required
                                    viewStyle={styles.field}
                                />
                                <FormInput
                                    label={translate('facialWebhookUrl')}
                                    placeholder='https://face-v4.sentinelx.com.br'
                                    form={form}
                                    fieldName='facialWebhookUrl'
                                    required
                                    viewStyle={styles.field}
                                />
                                <FormInput
                                    label={translate('areaInvasionWebhookUrl')}
                                    placeholder='https://area-v4.sentinelx.com.br'
                                    form={form}
                                    fieldName='areaInvasionWebhookUrl'
                                    required
                                    viewStyle={styles.field}
                                />
                            </View>
                        </SectionHeader>

                        <SectionHeader title={translate('generalConfigurations')} viewZIndex={0}>
                            <View style={styles.fieldsRow}>
                                <FormSwitch
                                    label={translate('createRtspWatcher')}
                                    form={form}
                                    fieldName='createRtspWatcher'
                                    viewStyle={styles.field}
                                />
                                <FormSwitch
                                    label={translate('adminPasswordRotation')}
                                    form={form}
                                    fieldName='adminPasswordRotation'
                                    viewStyle={styles.field}
                                />
                            </View>
                        </SectionHeader>
                    </ScrollView>
                </Card>}
            </Tabs>
        </Container>
    );
}

const styleSheet = createStyleSheet(theme => ({
    sectionHeaderTitle: {
        color: theme.colors.primary['500'],
        fontWeight: '600',
        fontSize: 16,
    },
    fieldsRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        columnGap: 24,
        rowGap: 16,
    },
    field: {
        maxWidth: 320,
        minWidth: 240,
        flexGrow: 1,
    },
    readOnlyLabel: {
        color: theme.colors.labelColor,
        fontSize: 14,
    },
    readOnlyValue: {
        color: theme.colors.textColor,
        fontSize: 16,
        paddingVertical: 8,
    },
}));
